import { Quote, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { BUSINESS_NAME, GOOGLE_BUSINESS_PROFILE_URL, SERVICE_AREA_TEXT } from "@/data/business";

export interface FamilyReview {
  /** Reviewer name as they permitted it to be published, e.g. "Jane S." */
  author: string;
  /** City, e.g. "Brentwood, TN" */
  location?: string;
  /** 1–5. Omit for unrated written references. */
  rating?: number;
  /** ISO date the review was given. */
  datePublished?: string;
  quote: string;
}

/**
 * Real, permissioned client reviews go here — nothing else.
 *
 * Leave this array empty until genuine reviews are collected. Never add
 * invented quotes. Once populated, the component renders the cards and
 * Review / aggregateRating JSON-LD automatically; no markup changes needed.
 */
export const familyReviews: FamilyReview[] = [];

/**
 * Review schema for the LocalBusiness. Returns null while there are no real
 * reviews, so no aggregateRating is ever published without underlying reviews.
 */
export const buildReviewSchema = () => {
  if (familyReviews.length === 0) return null;
  const rated = familyReviews.filter((r) => typeof r.rating === "number");
  return {
    "@type": "LocalBusiness",
    "@id": "https://www.kinshipcarecollective.com/#localbusiness",
    name: BUSINESS_NAME,
    review: familyReviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      datePublished: r.datePublished,
      reviewBody: r.quote,
      ...(r.rating
        ? { reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 } }
        : {}),
    })),
    ...(rated.length > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: (rated.reduce((s, r) => s + (r.rating ?? 0), 0) / rated.length).toFixed(1),
            reviewCount: rated.length,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
  };
};

const FamilyReviews = () => (
  <section className="py-20 md:py-28 bg-background" aria-labelledby="family-reviews-heading">
    <div className="container">
      <div className="max-w-2xl mx-auto text-center mb-12">
        <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-warm-ink mb-4 font-medium">In Their Words</p>
        <h2 id="family-reviews-heading" className="text-2xl md:text-3xl font-heading font-semibold mb-4">
          What Families Are Saying
        </h2>
        <div className="w-12 h-px bg-warm mx-auto" />
      </div>

      {familyReviews.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {familyReviews.map((review) => (
            <figure key={`${review.author}-${review.quote.slice(0, 24)}`} className="bg-card border border-border rounded-2xl p-7 flex flex-col">
              <Quote className="h-5 w-5 text-warm mb-4" aria-hidden />
              <blockquote className="text-foreground/85 leading-relaxed flex-1">{review.quote}</blockquote>
              {review.rating && (
                <div className="flex gap-0.5 mt-5" aria-label={`${review.rating} out of 5 stars`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < (review.rating ?? 0) ? "fill-warm text-warm" : "text-border"}`}
                      aria-hidden
                    />
                  ))}
                </div>
              )}
              <figcaption className="mt-4 text-sm font-medium">
                {review.author}
                {review.location && <span className="text-muted-foreground font-normal"> · {review.location}</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <div className="max-w-2xl mx-auto">
          <div className="bg-card border border-dashed border-border rounded-2xl p-8 md:p-10 text-center">
            <Quote className="h-6 w-6 text-warm mx-auto mb-5" aria-hidden />
            <p className="text-lg font-heading font-semibold mb-3">Reviews coming soon</p>
            <p className="text-foreground/75 leading-relaxed">
              We're a boutique, founder-led practice serving {SERVICE_AREA_TEXT}, and we publish only real, permissioned
              words from the families we serve — never invented quotes. As clients share their experience, their words
              will appear here.
            </p>
            <p className="text-muted-foreground text-sm mt-5">
              {GOOGLE_BUSINESS_PROFILE_URL ? (
                <>
                  Worked with {BUSINESS_NAME}?{" "}
                  <a
                    href={GOOGLE_BUSINESS_PROFILE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-4 text-primary"
                  >
                    Leave a review on Google
                  </a>
                  .
                </>
              ) : (
                <>
                  Considering care?{" "}
                  <Link to="/contact" className="underline underline-offset-4 text-primary">
                    Ask us for references
                  </Link>{" "}
                  during your private consultation.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <p className="text-center text-sm text-muted-foreground mt-8">
        A client of ours?{" "}
        <Link to="/share-your-experience" className="underline underline-offset-4 text-primary">
          Share your experience
        </Link>{" "}
        — we publish only with your permission.
      </p>
    </div>
  </section>
);

export default FamilyReviews;
